// Role-select modal — opens after a centre user (client_id) verifies
// their password. Lets the user choose between continuing as the
// centre admin (existing OTP flow) or logging in as a doctor mapped
// under that centre (new flow → /api/auth/doctor/*).
//
// Patients (UHID login) never see this — the parent component checks
// identifier_type === 'client_id' before opening the modal.
//
// Lifecycle:
//   Step 1 — two CTAs:
//     "Continue as Client"  → onContinueAsClient() — parent handles
//                             the existing OTP flow, modal closes.
//     "Login as Doctor"     → switches the modal to step 2.
//   Step 2 — phone input + Send OTP:
//     On submit we call /api/auth/doctor/send-otp, then call
//     onDoctorOtpSent({ phone, masked_phone, doctor_name }) so the
//     parent can switch its OTP screen into doctor-verify mode.
//
// Restyled to v5, and built like the site's strongest surfaces rather
// than a plain dialog: an ink header carrying the centre's identity over
// the same faint grid used on the hero plates, then two colour-blocked
// choice cards in the manner of the Corporate programme cards.
//
// The options are deliberately equal in weight — one teal, one ink. They
// used to be a filled teal block over a thin outlined one, which reads as
// primary over secondary, but nobody here is choosing a recommended path
// over a lesser one. They are telling us who they are, and a doctor
// should not feel they are taking the back door.

import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../../styles/tl5';
import { useSendDoctorOtpMutation } from '../../store/authApi';
import {
  CloseIcon, ArrowRightIcon, BuildingIcon, StethoscopeIcon,
} from './TlIcons';

const ghostBtn = {
  width: 40,
  height: 40,
  flexShrink: 0,
  display: 'grid',
  placeItems: 'center',
  border: 0,
  borderRadius: '50%',
  bgcolor: 'rgba(255,255,255,.08)',
  color: '#FFFFFF',
  cursor: 'pointer',
  transition: 'background-color .2s',
  '&:hover': { bgcolor: 'rgba(255,255,255,.18)' },
  '&:disabled': { opacity: 0.4, cursor: 'default' },
};

const RoleSelectModal = ({
  open,
  clientId,            // The client_id the user typed on the login form
  centreName,          // Friendly centre name (e.g. "Begumpet Diagnostics")
  onContinueAsClient,  // ()
  onDoctorOtpSent,     // ({ phone, masked_phone, doctor_name })
  onClose,             // ()
}) => {
  const [step, setStep] = useState('choose');  // 'choose' | 'phone'
  const [phone, setPhone] = useState('');
  const [err, setErr] = useState('');
  const [sendDoctorOtp, { isLoading }] = useSendDoctorOtpMutation();

  const reset = () => {
    setStep('choose');
    setPhone('');
    setErr('');
  };

  const handleClose = () => {
    if (isLoading) return;
    reset();
    onClose?.();
  };

  const onPickClient = () => {
    reset();
    onContinueAsClient?.();
  };

  const onPickDoctor = () => {
    setErr('');
    setStep('phone');
  };

  const onPhoneSubmit = async (e) => {
    e?.preventDefault?.();
    setErr('');
    if (!/^\d{10}$/.test(phone.trim())) {
      setErr('Enter a valid 10-digit mobile number');
      return;
    }
    try {
      const res = await sendDoctorOtp({
        client_id: clientId,
        phone: phone.trim(),
      }).unwrap();
      onDoctorOtpSent?.({
        phone: phone.trim(),
        masked_phone: res?.masked_phone || '',
        doctor_name: res?.doctor_name || '',
      });
      reset();
    } catch (e2) {
      setErr(e2?.data?.message || 'Could not send OTP. Please try again.');
    }
  };

  // The ink header. Carries who you are signing in as, over the same
  // faint grid the hero plates use, so the dialog feels like part of the
  // site rather than a system alert.
  const header = (title, eyebrow) => (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'var(--tl-g9)',
        color: '#FFFFFF',
        p: 'clamp(22px, 4vw, 30px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),'
            + 'linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
          backgroundSize: '34px 34px',
        }}
      />
      {/* teal glow, bottom-right */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          right: -70,
          bottom: -90,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(95,224,198,.22) 0%, rgba(95,224,198,0) 70%)',
        }}
      />

      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {step === 'phone' && (
          <Box
            component="button"
            type="button"
            aria-label="Back"
            onClick={() => { setStep('choose'); setErr(''); }}
            sx={{ ...ghostBtn, transform: 'rotate(180deg)' }}
          >
            <ArrowRightIcon size={18} />
          </Box>
        )}
        <Typography
          component="h2"
          sx={{
            flex: 1,
            m: 0,
            fontFamily: DISPLAY,
            fontWeight: 700,
            fontSize: 'clamp(22px, 3.4vw, 28px)',
            lineHeight: 1.15,
            letterSpacing: '-.02em',
            color: '#FFFFFF',
          }}
        >
          {title}
        </Typography>
        <Box component="button" type="button" aria-label="Close" onClick={handleClose} disabled={isLoading} sx={ghostBtn}>
          <CloseIcon size={20} />
        </Box>
      </Box>

      {(centreName || clientId) && (
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 0.6 }}>
          <Box
            component="span"
            sx={{
              fontFamily: UI,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: 'var(--tl-teal-l)',
            }}
          >
            {eyebrow}
          </Box>
          <Box
            component="span"
            sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(24px, 4vw, 32px)', lineHeight: 1.1, letterSpacing: '-.02em', color: '#FFFFFF' }}
          >
            {centreName || clientId}
          </Box>
          {centreName && clientId && (
            <Box
              component="span"
              sx={{
                alignSelf: 'start',
                mt: 0.5,
                p: '5px 11px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-g7)',
                color: 'var(--tl-teal-l)',
                fontFamily: UI,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '.04em',
              }}
            >
              {clientId}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  // Colour-blocked, in the manner of the Corporate programme cards.
  // Teal and ink read as two equal choices rather than a primary and a
  // fallback, which is what this screen actually is.
  const OptionCard = ({ icon, title, sub, onClick, tone }) => {
    const ink = tone === 'ink';
    return (
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          p: '20px 22px',
          borderRadius: '22px',
          border: 0,
          font: 'inherit',
          bgcolor: ink ? 'var(--tl-g9)' : 'var(--tl-teal)',
          color: '#FFFFFF',
          transition: 'transform .2s var(--tl-ease), box-shadow .2s var(--tl-ease)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 18px 36px -20px rgba(11, 43, 39, .55)',
          },
          '&:focus-visible': { outline: '3px solid var(--tl-teal-l)', outlineOffset: 2 },
          '&:hover .tl-chev': { transform: 'translateX(4px)' },
        }}
      >
        <Box
          component="span"
          sx={{
            width: 52,
            height: 52,
            flexShrink: 0,
            borderRadius: '16px',
            bgcolor: ink ? 'rgba(95,224,198,.16)' : 'rgba(255,255,255,.22)',
            color: ink ? 'var(--tl-teal-l)' : '#FFFFFF',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.45 }}>
          <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 19, lineHeight: 1.2, letterSpacing: '-.01em' }}>
            {title}
          </Box>
          <Box
            component="span"
            sx={{ fontFamily: UI, fontSize: 13, lineHeight: 1.45, color: ink ? '#9FC2BA' : '#E6FAF5' }}
          >
            {sub}
          </Box>
        </Box>
        <Box
          component="span"
          className="tl-chev"
          sx={{ flexShrink: 0, color: ink ? 'var(--tl-teal-l)' : '#FFFFFF', transition: 'transform .2s' }}
        >
          <ArrowRightIcon size={18} />
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      // MUI 5.18 ignores slotProps.paper here — PaperProps is the one
      // that actually lands.
      PaperProps={{
        sx: {
          borderRadius: '28px',
          overflow: 'hidden',
          bgcolor: '#FFFFFF',
          boxShadow: '0 40px 80px -36px rgba(11, 43, 39, .45)',
          m: 2,
          width: 'calc(100% - 32px)',
        },
      }}
    >
      {header(step === 'choose' ? 'How are you signing in?' : 'Login as a doctor',
              step === 'choose' ? 'Signing in to' : 'Doctor at')}

      <Box sx={{ p: 'clamp(22px, 4vw, 30px)', display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {/* ── step 1 — role picker ────────────────────────────────── */}
        {step === 'choose' && (
          <>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
              Centre admin, or a doctor at this centre? Both go through an OTP check —
              they just go to different numbers.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <OptionCard
                tone="teal"
                icon={<BuildingIcon size={24} />}
                title="Continue as the centre"
                sub="OTP to the centre’s registered number"
                onClick={onPickClient}
              />
              <OptionCard
                tone="ink"
                icon={<StethoscopeIcon size={24} />}
                title="Continue as a doctor"
                sub="OTP to your own mobile number"
                onClick={onPickDoctor}
              />
            </Box>
          </>
        )}

        {/* ── step 2 — doctor phone ───────────────────────────────── */}
        {step === 'phone' && (
          <Box component="form" onSubmit={onPhoneSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
              Enter the mobile number registered against your doctor profile here.
              We&rsquo;ll send a 6-digit OTP to it.
            </Typography>

            <Box component="label" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                Mobile number
              </Box>
              <Box
                component="input"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                inputMode="numeric"
                autoFocus
                disabled={isLoading}
                placeholder="10-digit number"
                sx={{
                  border: '1.5px solid var(--tl-line)',
                  borderRadius: '14px',
                  outline: 0,
                  font: 'inherit',
                  fontFamily: UI,
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '.08em',
                  p: '15px 16px',
                  color: 'var(--tl-g9)',
                  bgcolor: '#FFFFFF',
                  width: '100%',
                  minWidth: 0,
                  transition: 'border-color .2s',
                  '&::placeholder': { fontWeight: 400, letterSpacing: 'normal', color: 'var(--tl-muted-2)' },
                  '&:focus': { borderColor: 'var(--tl-teal)', boxShadow: '0 0 0 4px rgba(35,169,145,.14)' },
                  '&:disabled': { bgcolor: 'var(--tl-mint)' },
                }}
              />
            </Box>

            {err && (
              <Box component="span" role="alert" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: '#B23B37' }}>
                {err}
              </Box>
            )}

            <Box
              component="button"
              type="submit"
              disabled={isLoading || phone.length !== 10}
              sx={{
                border: 0,
                borderRadius: '16px',
                p: '16px 22px',
                font: 'inherit',
                fontFamily: UI,
                fontSize: 16,
                fontWeight: 800,
                textAlign: 'center',
                cursor: isLoading || phone.length !== 10 ? 'default' : 'pointer',
                bgcolor: isLoading || phone.length !== 10 ? 'var(--tl-line)' : 'var(--tl-g9)',
                color: isLoading || phone.length !== 10 ? 'var(--tl-muted-2)' : '#FFFFFF',
                transition: 'background-color .2s, transform .2s var(--tl-ease)',
                '&:not(:disabled):hover': { transform: 'translateY(-1px)' },
              }}
            >
              {isLoading ? 'Sending OTP\u2026' : 'Send OTP'}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default RoleSelectModal;
